import type React from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Box,
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyState,
  EmptyTitle,
} from "@salestrak/ui";

import { useAuth } from "#app/lib/auth-context";
import { useAuthEmail } from "#app/lib/hooks/use-auth-email";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useWorkspaceTombstone } from "#app/lib/hooks/use-workspace-tombstone";
import { formatDate } from "#app/lib/utils/format-date";
import { SalesTrakLogo } from "#app/components/brand/salestrak-logo";
import { WorkspaceScreenLayout } from "#app/components/workspace-screen-layout";
import { routePath } from "#app/lib/hooks/use-app-navigate";
import { AnalyticsContext, AuthEvent, trackEvent } from "#app/lib/analytics";

/**
 * App-level failure screens: the whole session is unusable, not one query or
 * one screen. Render errors stay with the error boundaries and their stack
 * well; data-level load failures stay with `LoadErrorState`. The workspace
 * screen wears the brand mark because it stands in for the app itself; the
 * network screen names its cause with the connectivity glyph instead.
 *
 * Bespoke `Empty` parts, not the `EmptyState` façade — the media here is the
 * product mark, which the façade's icon-chip treatments deliberately cannot
 * express.
 */
function BrandErrorBody({
  title,
  description,
  actions,
}: {
  title: string;
  description: string;
  actions?: React.ReactNode;
}) {
  return (
    <Empty>
      <EmptyHeader>
        <EmptyMedia>
          <SalesTrakLogo showText={false} height={48} />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      {actions && (
        <EmptyContent>
          <Box direction="row" gap="sm" justify="center" wrap>
            {actions}
          </Box>
        </EmptyContent>
      )}
    </Empty>
  );
}

/**
 * Account data never arrived, so there is no workspace, no shell, nothing to
 * navigate to. A full reload is the honest retry — the failed init lives in
 * a provider effect, not a refetchable query. Log out is the escape hatch
 * for the failure that only looks like a network problem (a wedged session):
 * `signOut` clears local tokens even when the network is down.
 */
export function NetworkErrorScreen() {
  const { signOut } = useAuth();
  return (
    <Box align="center" fill="flex" justify="center" padding="lg">
      <EmptyState
        actions={
          <>
            <Button onClick={() => window.location.reload()}>Reload</Button>
            <Button
              onClick={() => {
                trackEvent({
                  context: AnalyticsContext.Auth,
                  event: AuthEvent.SignOut,
                  data: { via: "network_error" },
                });
                void signOut();
              }}
              variant="outline"
            >
              Log out
            </Button>
          </>
        }
        description="Something might be wrong with your connection. Reload the app to try again."
        icon="wifi-off"
        media="icon"
        title="Network error while loading"
        color="destructive"
      />
    </Box>
  );
}

/**
 * The URL names a workspace this account cannot see — mistyped, deleted, or
 * one the user is not a member of; the list the API returned is the same
 * either way, so the copy claims no more than it knows — except for a purged
 * workspace, whose tombstone lets the screen say so, and when. The layout's
 * header carries the escapes the signed-in state allows: back into the active
 * workspace when one exists (otherwise back to login, which signs out), and
 * the account menu's log out for the "wrong account" case.
 */
export function WorkspaceNotFoundScreen({ urlPath }: { urlPath?: string }) {
  const email = useAuthEmail();
  const { activeWorkspace } = useWorkspace();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const tombstone = useWorkspaceTombstone(urlPath);

  return (
    <WorkspaceScreenLayout
      backLabel={activeWorkspace ? "Back to SalesTrak" : "Back to login"}
      backSignsOut={!activeWorkspace}
      email={email}
      onBack={
        activeWorkspace
          ? () =>
              navigate({
                to: routePath(`/${activeWorkspace.urlPath}`),
              })
          : signOut
      }
    >
      <BrandErrorBody
        actions={
          <Button
            onClick={() => navigate({ to: "/workspace-select" })}
            color="neutral"
            ink="foreground"
          >
            Choose a Workspace
          </Button>
        }
        description={
          tombstone
            ? `This workspace was permanently deleted on ${formatDate(tombstone.deletedAt)}. Its data cannot be recovered.`
            : "This workspace does not exist, or you don't have access to it."
        }
        title={tombstone ? `${tombstone.name} was deleted` : "Authentication error"}
      />
    </WorkspaceScreenLayout>
  );
}
